<template>
    <div>
        <div class="h-30 desktop:h-40 px-6 tablet:px-24 py-6 mt-12 desktop:mt-0 bg-gradient-teal space-y-3">
            <div class="font-bold text-lg tablet:text-5xl leading-tight">
                You Qualify for an Internet equipment&nbsp;Upgrade
            </div>
            <div class="text-sm tablet:text-lg tablet:font-bold">
                Let’s make sure you’re getting all of your&nbsp;speed
            </div>
        </div>
        <div class="tablet:w-3/5 px-6 tablet:px-24 py-12 space-y-8">
            <div v-if="requestSuccessful"
                 class="space-y-4">
                 <h5>
                    Thanks. That was a breeze!
                </h5>
                <p>
                    Your new modem will ship within 5 business&nbsp;days.
                </p>
                <p>
                    Your new modem will come with a free self install kit to help you replace your old modem. You can also view our
                    <a href="https://www.breezeline.com/modeminstall"
                       target="_blank"
                       class="underline">
                        Support Videos
                    </a>
                    if you need more&nbsp;help.
                </p>
                <p class="font-bold text-salmon">Important: There is no need to return your old modem to us, and you will not be charged. Please recycle the modem(s) by following any local ordinances regarding the handling of electronic&nbsp;waste.</p>
            </div>
            <div v-else
                 class="space-y-4">
                <h5>WHAT</h5>
                <p>We are offering you an Internet equipment upgrade so you can get the most from your Breezeline internet&nbsp;service.</p>
                <h5>WHY</h5>
                <p>As internet technology evolves, it’s important that we upgrade hardware inside and outside of your home to keep up. Upgrading
                   your Internet equipment is essential to making sure you get what you deserve: a reliable connection that’s&nbsp;fast.</p>
                <h5>HOW</h5>
                <p>It's easy! To request your new Internet equipment, please fill out the form below with the last name and phone number on your Breezeline
                   account.  We will ship your new Internet equipment, free of&nbsp;charge.</p>
                <h5>Please enter the account holder’s information:</h5>
                <form class="space-y-5"
                      id="equipment-swap-form"
                      v-show="!isReadyToRequest"
                      autocomplete="off"
                      @submit.prevent="submitForm()">
                    <div class="space-y-1">
                        <label class="text-sm font-bold"
                               :class="{ 'text-salmon': $v.lastName.$error }">
                            Last Name
                        </label>
                        <input v-model.trim="$v.lastName.$model"
                               type="text"
                               tabindex="1"
                               :disabled="isReadyToRequest"
                               :class="{ 'border-salmon': $v.lastName.$error, 'opacity-50': isReadyToRequest }"
                               class="block w-full tablet:w-96 sm:text-sm disabled:opacity-50"
                               @keydown.enter.prevent />
                        <div v-if="$v.lastName.$error && !$v.lastName.required"
                             class="text-salmon text-sm">
                            <i class="fas fa-exclamation-circle" />
                            Please enter a valid last name
                        </div>
                    </div>
                    <div class="space-y-1">
                        <label class="text-sm font-bold"
                               :class="{ 'text-salmon': $v.phoneNumber.$error }">
                            Phone Number
                        </label>
                        <input v-model.trim="$v.phoneNumber.$model"
                               type="text"
                               tabindex="1"
                               maxlength="10"
                               :disabled="isReadyToRequest"
                               :class="{ 'border-salmon': $v.phoneNumber.$error && !isAccountNumber, 'opacity-50': isReadyToRequest }"
                               class="block w-full tablet:w-96 sm:text-sm disabled:opacity-50"
                               @keydown.enter.prevent />
                        <div v-if="$v.phoneNumber.$error && !$v.phoneNumber.required"
                             class="text-salmon text-sm">
                            <i class="fas fa-exclamation-circle" />
                            Please enter a valid phone number
                        </div>
                        <div v-if="$v.phoneNumber.$error && !$v.phoneNumber.numeric"
                             class="text-salmon text-sm">
                            <i class="fas fa-exclamation-circle" />
                            Only numeric values are allowed
                        </div>
                        <div v-if="$v.phoneNumber.$error && !$v.phoneNumber.minLength && $v.phoneNumber.numeric"
                             class="text-salmon text-sm">
                            <i class="fas fa-exclamation-circle" />
                            Phone number must be 10 digits
                        </div>
                    </div>
                    <div class="relative w-full tablet:w-96">
                        <div class="absolute inset-0 flex items-center"
                            aria-hidden="true">
                            <div class="w-full border-t border-gray-300" />
                        </div>
                        <div class="relative flex justify-center">
                            <span class="px-2 bg-gray-100 text-sm text-center">
                                Or, enter the 16-digit account number<br>found on your monthly Breezeline statement
                            </span>
                        </div>
                    </div>
                    <div class="space-y-1">
                        <label class="text-sm font-bold"
                               :class="{ 'text-salmon': $v.accountNumber.$error }">
                            Account Number
                        </label>
                        <input v-model.trim="$v.accountNumber.$model"
                               type="text"
                               tabindex="1"
                               maxlength="16"
                               :disabled="isReadyToRequest"
                               :class="{ 'border-salmon': $v.accountNumber.$error, 'opacity-50': isReadyToRequest }"
                               class="block w-full tablet:w-96 sm:text-sm"
                               @keydown.enter.prevent />
                        <div v-if="$v.accountNumber.$error && !$v.accountNumber.required"
                             class="text-salmon text-sm">
                            <i class="fas fa-exclamation-circle" />
                            Please enter a valid account number
                        </div>
                        <div v-if="$v.accountNumber.$error && $v.accountNumber.required && !$v.accountNumber.numeric"
                             class="text-salmon text-sm">
                            <i class="fas fa-exclamation-circle" />
                            Only numeric values are allowed
                        </div>
                        <div v-if="$v.accountNumber.$error && $v.accountNumber.required && $v.accountNumber.numeric && !$v.accountNumber.minLength"
                            class="text-salmon text-sm">
                            <i class="fas fa-exclamation-circle" />
                            Your account number must be 16 digits
                        </div>
                    </div>
                </form>
                    <div v-if="isReadyToRequest"
                         class="space-y-1">
                        <p class="text-green-600 p-2 border border-green-600 bg-green-50">
                            You are eligible to request new Internet equipment. No need to enter your information. Click request Internet equipment to continue.</p>
                    </div>
                    <div>
                        <button type="submit"
                                form="equipment-swap-form"
                                class="btn btn-primary">
                            {{ isReadyToRequest ? 'Request My New Internet equipment' : 'Check Internet equipment Availability' }}
                        </button>
                    </div>
                    <div v-if="submitStatus" class="font-bold text-salmon">
                        {{ submitStatus }}
                    </div>
            </div>
        </div>
    </div>
</template>

<script>
    import axios from 'axios'
    import {
        minLength,
        numeric,
        integer,
        requiredUnless,
        required
    } from 'vuelidate/lib/validators'

    axios.defaults.timeout = 1000 * 10

    export default {
        name: 'EquipmentSwap',
        data () {
            return {
                lastName: '',
                phoneNumber: '',
                accountNumber: '',
                uuid: null,
                isReadyToRequest: false,
                requestSuccessful: false,
                submitStatus: null,
            }
        },
        validations: {
            lastName: {
                required: requiredUnless('isAccountNumber'),
            },
            phoneNumber: {
                numeric,
                required: requiredUnless('isAccountNumber'),
                minLength: minLength(10),
            },
            accountNumber: {
                required: requiredUnless('isLastNameAndPhoneNumber'),
                numeric,
                minLength: minLength(16),
            },
        },
        computed: {
            // Test whether user is trying to authenticate with (last name and phone number) or (account number)
            isLastNameAndPhoneNumber () {
                return this.lastName.length > 0 && this.phoneNumber.length > 0
            },
            isAccountNumber () {
                return this.accountNumber.length > 0
            },
        },
        methods: {
            submitForm() {
                if (this.isReadyToRequest) {
                    this.submitRequest()
                } else {
                    this.checkAvailability()
                }
            },
            checkAvailability() {
                this.$v.$touch()

                if (!this.$v.$invalid) {
                    this.$store.dispatch('toggleLoading', { value: true }, { root: true })


                    let loginData = {
                        accountNumber: this.accountNumber || null,
                        lastName: this.lastName || null,
                        phoneNumber: this.phoneNumber || null,
                    };
                    // Drop null values from loginData:
                    loginData = Object.keys(loginData)
                        .filter(key => loginData[key] !== null)
                        .reduce((obj, key) => {
                            obj[key] = loginData[key];
                            return obj;
                        }, {});

                    axios
                        .post(`${window.apiUrl}/api/equipment-swap/login`, loginData)
                        .then(res => {
                            this.uuid = res.data.uuid
                            this.isReadyToRequest = true
                        })
                        .catch(err => {
                            const res = err.response

                            if (res.data.code === '11' || res.data.code === '13' || res.status === '404') {
                                this.$store.dispatch('togglePopup', {
                                    type: `EQUIPMENT_SWAP/ERRORS/USER_NOT_FOUND`,
                                }, { root: true })
                            } else {
                                this.$store.dispatch('togglePopup', {
                                    type: `EQUIPMENT_SWAP/ERRORS/SERVER`,
                                }, { root: true })
                            }
                        })
                        .finally(() => {
                            this.$store.dispatch('toggleLoading', { value: false }, { root: true })
                        })
                }
            },
            submitRequest() {
                axios
                    .post(`${window.apiUrl}/api/equipment-swap/order`, {
                        uuid: this.uuid,
                    })
                    .then(res => {
                        if (res.data.code === '01') {
                            this.requestSuccessful = true
                            const successUrl = `${window.location.pathname}?success=true`
                            window.history.pushState({ path: successUrl }, '', successUrl)
                            // Push gtag event on success
                            this.sendEvent('Form Submission', 'Form Submit Success', 'form_submit', null, 'Free Modem Upgrade Success')
                        } else {
                            this.$store.dispatch('togglePopup', {
                                type: `EQUIPMENT_SWAP/ERRORS/SERVER`,
                            })
                        }

                    })
                    .catch(err => {
                        const res = err.response

                        if (res.data.code === '03') {
                            this.$store.dispatch('togglePopup', {
                                type: `EQUIPMENT_SWAP/ERRORS/REQUEST_EXCEEDED`,
                            }, { root: true })
                        } else if (res.data.code === '13') {
                            this.$store.dispatch('togglePopup', {
                                type: `EQUIPMENT_SWAP/ERRORS/USER_NOT_FOUND`,
                            }, { root: true })
                        } else {
                            this.$store.dispatch('togglePopup', {
                                type: `EQUIPMENT_SWAP/ERRORS/SERVER`,
                            }, { root: true })
                        }
                    })
                    // Check if the page is in the success state
                    window.addEventListener('popstate', (event) => {
                        // Reset the UI
                        this.requestSuccessful = false;
                        this.isReadyToRequest = false;
                        this.uuid = '';
                    });

                    // Push the second gtag event (assuming it should be sent regardless of success or failure)
                    this.sendEvent('Form Submission', 'Form Submit Success', 'form_submit', null, 'Check Modem Availability')
            },
        },
    }
</script>
